<script setup lang="ts">
/**
 * ShortcutsDialog — `?` help overlay listing every keyboard shortcut.
 *
 * Native <dialog> via showModal() so we get focus trap, Escape-to-close,
 * and inert backdrop for free without pulling in another modal lib.
 * Imperatively `open()`-able from the parent — no v-model needed
 * because there's no two-way state to sync.
 */
import { useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { SHORTCUT_GROUPS } from "@/composables/useKeyboardShortcuts";

const { t } = useI18n({ useScope: 'global' });
const dialogRef = useTemplateRef<HTMLDialogElement>("shortcutsDialog");

function open() {
  dialogRef.value?.showModal();
}
function close() {
  dialogRef.value?.close();
}

defineExpose({ open, close });
</script>

<template>
  <dialog
    ref="shortcutsDialog"
    class="rounded-lg border border-border bg-surface-raised p-0 text-content-primary shadow-elevated
           backdrop:bg-black/60 max-w-lg w-[90vw]"
    aria-labelledby="shortcuts-dialog-title"
  >
    <div class="p-5 space-y-4">
      <div class="flex items-baseline justify-between gap-3">
        <h2 id="shortcuts-dialog-title" class="text-base font-semibold">
          {{ t('shortcuts.title') }}
        </h2>
        <i18n-t keypath="shortcuts.escToClose" tag="p" class="text-xs text-content-tertiary">
          <template #esc><kbd class="kbd">Esc</kbd></template>
        </i18n-t>
      </div>

      <div
        v-for="group in SHORTCUT_GROUPS"
        :key="group.labelKey"
        class="space-y-2"
      >
        <p class="text-xs uppercase tracking-wider text-content-tertiary">
          {{ t(group.labelKey) }}
        </p>
        <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <template v-for="(s, i) in group.items" :key="i">
            <dt class="flex items-center gap-1">
              <template v-for="(k, j) in s.keys" :key="j">
                <kbd class="kbd">{{ k }}</kbd>
                <span
                  v-if="j < s.keys.length - 1"
                  class="text-content-tertiary text-xs"
                  aria-hidden="true"
                >{{ t('shortcuts.chordSeparator') }}</span>
              </template>
            </dt>
            <dd class="text-content-secondary self-center">{{ t(s.descriptionKey) }}</dd>
          </template>
        </dl>
      </div>

      <div class="flex justify-end pt-1">
        <button type="button" class="btn-secondary" @click="close">
          {{ t('shortcuts.close') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
/* Mac/Win/Linux-friendly key cap. Scoped so it doesn't bleed into
   places we don't want it (e.g. accidental <kbd> in a CHANGELOG). */
.kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid var(--color-border);
  border-bottom-width: 2px;
  border-radius: 0.25rem;
  background: var(--color-surface-overlay);
  color: var(--color-content-primary);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  line-height: 1.1;
}
</style>
